import { Cell, CellContext, flexRender } from '@tanstack/react-table';

import { cn } from 'support/helpers/cn/cn';

import { getBaseBorderClasses, getCellBorderClasses } from '../../utils/borderUtils';
import { getPinningStyles } from '../../layoutHelpers/getPinningStyles';
import { RowWithEmbeddedTableRowInfo } from '../../types';

export type Props<TData, TValue> = {
  cell: Cell<TData, TValue>;
  isLastRow: boolean;
  betweenCellBorders: boolean;
  isFirstColumn: boolean;
  isLastColumn: boolean;
  isAction: boolean;
  hasRowErrorBorder?: boolean;
  className?: string;
  style?: React.CSSProperties;
  rowSpan?: number;
  colSpan?: number;
  rowWithEmbeddedTableRowInfo?: RowWithEmbeddedTableRowInfo<TData>;
  children?: React.ReactNode;
};

export const Td = <TData, TValue>({
  cell,
  isLastRow,
  betweenCellBorders,
  className,
  isFirstColumn,
  isLastColumn,
  isAction,
  hasRowErrorBorder = false,
  style,
  rowSpan,
  colSpan,
  rowWithEmbeddedTableRowInfo,
  children,
}: Props<TData, TValue>) => {
  const borderClasses = getCellBorderClasses({
    isFirstColumn,
    isLastColumn,
    isLastRow,
    betweenCellBorders,
    hasRowErrorBorder,
    isAction,
  });

  const cellContext = cell.getContext();
  const contextWithEmbeddedTableRow: CellContext<TData, TValue> = rowWithEmbeddedTableRowInfo
    ? { ...cellContext, rowWithEmbeddedTableRowInfo }
    : cellContext;

  return (
    <td
      key={cell.id}
      data-cellid={cell.id}
      rowSpan={rowSpan}
      colSpan={colSpan}
      className={cn('p-0 font-normal whitespace-nowrap', getBaseBorderClasses(), borderClasses, className)}
      style={{
        ...style,
        ...getPinningStyles(cell.column),
        height: 0, // we need to set a height so the inner div can control the height
      }}
    >
      <div
        className={cn('block h-full', {
          'text-right': cell.column.columnDef.meta?.style?.textAlign === 'right',
          'text-left': cell.column.columnDef.meta?.style?.textAlign !== 'right',
        })}
      >
        {children ?? flexRender(cell.column.columnDef.cell, contextWithEmbeddedTableRow)}
      </div>
    </td>
  );
};
